import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ShieldAlert, RefreshCw, Cloud, Search, Save, X, Flame,
  ShieldCheck, ExternalLink, AlertTriangle, Activity, HelpCircle, Wand2,
  ListOrdered, Layers,
} from "lucide-react";
import CVEDashboardWidgets from "./CVEDashboardWidgets";
import CEBreachesTab from "./CEBreachesTab";
import PriorityQueueTab from "./PriorityQueueTab";
import EstateSprawlTab from "./EstateSprawlTab";
import ResolvedByAliceTab from "./ResolvedByAliceTab";

/**
 * CVE Management page (super-admin view).
 *
 * Three things in one screen:
 *  1. Sync status / trigger for NVD + EPSS + KEV (top card)
 *  2. Family -> CPE mapping table with inline edit + per-family CVE summary
 *  3. Selected-family drill-down: per-version verdicts (Notepad++ 7.2 = bad,
 *     8.9.6.4 = good) and the candidate CVE list.
 */
// Rows rendered in the Families card before the "type to narrow" notice.
const FAMILY_RENDER_CAP = 400;

const SEVERITY_COLOR = {
  CRITICAL: "bg-red-600 text-white",
  HIGH: "bg-orange-600 text-white",
  MEDIUM: "bg-yellow-600 text-white",
  LOW: "bg-blue-600 text-white",
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const SeverityBadge = ({ severity, unscored = false }) => {
  // Treat null / undefined / "" / "NONE" / "UNKNOWN" as "no classification"
  // so the UI shows a neutral em-dash instead of an empty/"NONE" badge.
  const sev = (severity || "").toUpperCase();
  if (!sev || sev === "NONE" || sev === "UNKNOWN") {
    if (unscored) {
      return (
        <Badge
          className="bg-amber-600/80 text-white text-xs"
          data-testid="severity-badge-unscored"
          title="One or more CVEs match this version, but NVD has not published a CVSS score yet."
        >
          Unscored
        </Badge>
      );
    }
    return <span className="text-gray-400 text-xs">—</span>;
  }
  return (
    <Badge
      className={`${SEVERITY_COLOR[sev] || "bg-gray-600 text-white"} text-xs`}
      data-testid={`severity-badge-${sev}`}
    >
      {sev}
    </Badge>
  );
};

const KevBadge = ({ on }) => (
  on ? (
    <Badge className="bg-rose-700 text-white text-xs inline-flex items-center gap-1" data-testid="kev-badge">
      <Flame className="w-3 h-3" /> KEV
    </Badge>
  ) : null
);

const SyncStatusDot = ({ status }) => {
  const dot =
    status === "ok" ? "bg-green-500"
    : status === "running" ? "bg-blue-500 animate-pulse"
    : status === "error" ? "bg-red-500"
    : "bg-gray-500";
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${dot}`} aria-label={status || "not run"} />;
};

const SyncSourcePill = ({ label, state }) => (
  <div
    className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-[rgba(229,239,250,0.08)] border border-[rgba(229,239,250,0.12)]"
    data-testid={`sync-state-${label.toLowerCase()}`}
  >
    <SyncStatusDot status={state?.status} />
    <div className="flex flex-col min-w-0">
      <span className="text-sm font-semibold text-white leading-tight">{label}</span>
      <span className="text-xs text-white/70 truncate">{fmtDateTime(state?.last_synced_at)}</span>
    </div>
  </div>
);

const CVEManagementPage = ({ embedded = false }) => {
  const { getAccessToken } = useAuth();

  // Sub-tab: overview (dashboard + families) | breaches | unmapped.
  // Kept as URL-agnostic local state so the parent Discover page's
  // own tab plumbing isn't disturbed. Deep-link support can be added
  // later once we agree on the URL scheme.
  const [subTab, setSubTab] = useState("overview");

  const [syncStatus, setSyncStatus] = useState(null);
  const [families, setFamilies] = useState([]);
  const [familiesTotal, setFamiliesTotal] = useState(0);
  const [familiesLoading, setFamiliesLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [vulnerableOnly, setVulnerableOnly] = useState(false);
  const [unmappedOnly, setUnmappedOnly] = useState(false);
  const [editing, setEditing] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [rematching, setRematching] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  // family_name -> ranked CPE candidates, so an unmapped row is one click
  // rather than free-text CPE entry.
  const [cpeSuggestions, setCpeSuggestions] = useState({});
  const [autoLinking, setAutoLinking] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    return { Authorization: `Bearer ${token}` };
  }, [getAccessToken]);

  const fetchStatus = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const r = await axios.get(`${API}/cve/sync/status`, { headers });
      setSyncStatus(r.data || {});
    } catch (err) {
      console.warn("sync status fetch failed", err);
    }
  }, [authHeaders]);

  const fetchDashboard = useCallback(async () => {
    setDashboardLoading(true);
    try {
      const headers = await authHeaders();
      const r = await axios.get(`${API}/cve/dashboard`, { headers });
      setDashboard(r.data || null);
    } catch (err) {
      // Dashboard failure shouldn't break the page — surface a soft warning
      // and hide the widgets. The families table below still works.
      console.warn("dashboard fetch failed", err);
      setDashboard(null);
    } finally {
      setDashboardLoading(false);
    }
  }, [authHeaders]);

  // Fetched once per filter toggle — the search box filters the list
  // client-side (below) instead of refetching on every keystroke, and the
  // page_size is high enough that no estate is silently truncated.
  const fetchFamilies = useCallback(async () => {
    setFamiliesLoading(true);
    try {
      const headers = await authHeaders();
      const params = { page_size: 10000 };
      if (vulnerableOnly) params.vulnerable_only = true;
      if (unmappedOnly) params.has_cpe = false;
      const r = await axios.get(`${API}/cve/families`, { headers, params });
      const items = r.data?.items || [];
      setFamilies(items);
      setFamiliesTotal(r.data?.total || items.length);
    } catch (err) {
      toast.error("Failed to load families");
    } finally {
      setFamiliesLoading(false);
    }
  }, [authHeaders, vulnerableOnly, unmappedOnly]);

  const filteredFamilies = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = families.filter((f) => (f.family_name || "").trim().length > 0);
    if (!q) return base;
    return base.filter(
      (i) =>
        (i.family_name || "").toLowerCase().includes(q) ||
        (i.cpe || "").toLowerCase().includes(q)
    );
  }, [families, search]);

  // Rendering thousands of rows with inline editors is the slow part of
  // this card, not the fetch. Show the first FAMILY_RENDER_CAP and let the
  // search narrow the rest.
  const visibleFamilies = useMemo(
    () => filteredFamilies.slice(0, FAMILY_RENDER_CAP),
    [filteredFamilies]
  );

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { fetchFamilies(); }, [fetchFamilies]);
  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // Suggestions come from the vendor:product vocabulary in the synced CVE
  // data, matched on family name + publisher. Fetched for the families on
  // screen so the list stays responsive on a large estate.
  //
  // Declared ABOVE the effect that depends on it: a dependency array is
  // evaluated during render, so referencing a `const` declared further down
  // throws a temporal-dead-zone ReferenceError and blanks the page.
  //
  // Which families have been requested is tracked in a ref rather than read
  // out of `cpeSuggestions`, so this keeps a stable identity. Depending on
  // the state it sets would re-run the effect on every update, and two
  // renders before the state landed would each fire the same requests.
  //
  // ONE batch request, not one per unmapped row: the server builds the CPE
  // vocabulary per call, so per-family requests rebuilt it hundreds of
  // times on a large estate. The batch form is also cached server-side.
  const suggestionsLoaded = useRef(false);
  const fetchCpeSuggestions = useCallback(async () => {
    if (suggestionsLoaded.current) return;
    suggestionsLoaded.current = true;
    try {
      const headers = await authHeaders();
      const r = await axios.get(`${API}/cve/cpe-suggestions`, { headers });
      setCpeSuggestions(r.data?.suggestions || {});
    } catch {
      // Suggestions are an assist, not a requirement — the manual CPE input
      // stays available either way. Allow a retry on the next load.
      suggestionsLoaded.current = false;
    }
  }, [authHeaders]);

  // Pull candidates once any unmapped family is on screen.
  useEffect(() => {
    if (families.some((f) => !f.cpe)) fetchCpeSuggestions();
  }, [families, fetchCpeSuggestions]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const headers = await authHeaders();
      await axios.post(`${API}/cve/sync?background=true`,
        { include_nvd: true, include_epss: true, include_kev: true },
        { headers });
      toast.success("Sync started in the background");
      setTimeout(fetchStatus, 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start sync");
    } finally {
      setSyncing(false);
    }
  };

  const handleRematch = async () => {
    setRematching(true);
    try {
      const headers = await authHeaders();
      const r = await axios.post(`${API}/cve/rematch`, {}, { headers });
      toast.success(
        `Evaluated ${r.data?.families_evaluated || 0} families, ${r.data?.families_vulnerable || 0} vulnerable`
      );
      fetchFamilies();
      fetchDashboard();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rematch failed");
    } finally {
      setRematching(false);
    }
  };

  const handleAutoLink = async () => {
    setAutoLinking(true);
    try {
      const headers = await authHeaders();
      const r = await axios.post(`${API}/cve/auto-link`, null, { headers });
      const { linked, considered, skipped_ambiguous: needsReview } = r.data || {};
      if (linked) {
        toast.success(
          `Mapped ${linked} of ${considered} unmapped families` +
          (needsReview ? ` — ${needsReview} need review` : "")
        );
      } else {
        toast.info(
          considered
            ? `No confident matches among ${considered} unmapped families`
            : "Every family is already mapped"
        );
      }
      setCpeSuggestions({});
      suggestionsLoaded.current = false;
      fetchFamilies();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auto-map failed");
    } finally {
      setAutoLinking(false);
    }
  };

  const handleSaveCpe = async (familyName, explicitValue) => {
    // A suggestion chip passes its CPE directly: setEditing is async, so
    // reading it back here would save the previous value.
    const value = explicitValue !== undefined ? explicitValue : editing[familyName];
    try {
      const headers = await authHeaders();
      await axios.put(
        `${API}/cve/families/${encodeURIComponent(familyName)}/cpe`,
        { cpe: value || null },
        { headers }
      );
      toast.success(value ? `CPE saved for ${familyName}` : `CPE cleared for ${familyName}`);
      setCpeSuggestions((prev) => {
        const next = { ...prev }; delete next[familyName]; return next;
      });
      setEditing((s) => {
        const next = { ...s };
        delete next[familyName];
        return next;
      });
      fetchFamilies();
      if (selectedFamily === familyName) loadDetail(familyName);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const loadDetail = async (familyName) => {
    setSelectedFamily(familyName);
    setDetailLoading(true);
    setDetail(null);
    try {
      const headers = await authHeaders();
      const r = await axios.get(
        `${API}/cve/families/${encodeURIComponent(familyName)}`,
        { headers }
      );
      setDetail(r.data);
    } catch (err) {
      toast.error("Failed to load family detail");
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className={embedded ? "" : "min-h-screen bg-transparent p-6"} data-testid="cve-management-page">
      <div className={embedded ? "" : "max-w-[1600px] mx-auto"}>
        {/* Header — hidden in embedded mode so the parent (Discover) keeps its own page title */}
        {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] flex items-center justify-center">
              <ShieldAlert className="w-6 h-6 text-cyan-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">CVE Vulnerability Management</h1>
              <p className="text-sm text-white/70">
                CPE-based matching against NVD with EPSS and CISA KEV enrichment.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleRematch}
              disabled={rematching}
              variant="outline"
              className="bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white hover:bg-white/10"
              data-testid="rematch-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${rematching ? "animate-spin" : ""}`} />
              Re-evaluate families
            </Button>
            <Button
              onClick={handleSync}
              disabled={syncing}
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
              data-testid="sync-button"
            >
              <Cloud className={`w-4 h-4 mr-2 ${syncing ? "animate-pulse" : ""}`} />
              Sync NVD / EPSS / KEV
            </Button>
          </div>
        </div>
        )}

        {/* Embedded-mode action bar: just the two action buttons, no title */}
        {embedded && (
          <div className="flex justify-end gap-2 mb-4">
            <Button
              onClick={handleRematch}
              disabled={rematching}
              variant="outline"
              size="sm"
              className="bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white hover:bg-white/10"
              data-testid="rematch-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${rematching ? "animate-spin" : ""}`} />
              Re-evaluate families
            </Button>
            <Button
              onClick={handleSync}
              disabled={syncing}
              size="sm"
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
              data-testid="sync-button"
            >
              <Cloud className={`w-4 h-4 mr-2 ${syncing ? "animate-pulse" : ""}`} />
              Sync NVD / EPSS / KEV
            </Button>
          </div>
        )}

        {/* Sub-tab bar — Overview / Cyber Essentials Breaches / Needs Mapping.
            Sits above the sync-status card so the user always sees which
            slice of Vulnerabilities they're looking at. */}
        <div
          className="mb-4 inline-flex rounded-lg bg-white/[0.06] p-1 border border-white/[0.1]"
          role="tablist"
          data-testid="cve-subtabs"
        >
          {[
            { id: "overview", label: "Overview", icon: Activity },
            { id: "priority", label: "Fix Queue", icon: ListOrdered },
            {
              id: "breaches",
              label: "Cyber Essentials Breaches",
              icon: ShieldAlert,
              count: dashboard?.ce_breach_summary?.app_versions_affected || 0,
              tone: "danger",
            },
            {
              id: "unmapped",
              label: "Needs Mapping",
              icon: HelpCircle,
              count: dashboard?.mapping_coverage?.unmapped_families || 0,
              tone: "warning",
            },
            { id: "sprawl", label: "Estate Sprawl", icon: Layers },
            { id: "resolved", label: "Resolved by ALICE", icon: ShieldCheck },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = subTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setSubTab(tab.id);
                  if (tab.id === "unmapped") {
                    setUnmappedOnly(true);
                    setVulnerableOnly(false);
                  } else {
                    setUnmappedOnly(false);
                  }
                }}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition ${
                  active
                    ? "bg-cyan-600/70 text-white shadow"
                    : "text-white/70 hover:text-white hover:bg-white/[0.06]"
                }`}
                data-testid={`cve-subtab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span
                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                      tab.tone === "danger"
                        ? "bg-red-600 text-white"
                        : "bg-amber-500 text-black"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {subTab === "breaches" ? (
          <CEBreachesTab />
        ) : subTab === "priority" ? (
          <PriorityQueueTab
            onJumpToUnmapped={() => {
              setSubTab("unmapped");
              setUnmappedOnly(true);
              setVulnerableOnly(false);
            }}
          />
        ) : subTab === "sprawl" ? (
          <EstateSprawlTab />
        ) : subTab === "resolved" ? (
          <ResolvedByAliceTab />
        ) : (
        <>
        {/* Compact data source status — single horizontal strip */}
        <div
          className="mb-6 bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] rounded-xl p-4"
          data-testid="sync-status-card"
        >
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-cyan-300" />
            <span className="text-sm font-semibold text-white">Data source status</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SyncSourcePill label="NVD" state={syncStatus?.nvd} />
            <SyncSourcePill label="EPSS" state={syncStatus?.epss} />
            <SyncSourcePill label="KEV" state={syncStatus?.kev} />
          </div>
          {syncStatus?.nvd?.error && (
            <div className="text-xs text-red-300 mt-3 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              NVD error: {syncStatus.nvd.error}
            </div>
          )}
        </div>

        {/* Dashboard widgets — visual roll-up of tenant vulnerability posture.
            Only rendered on the Overview tab (Needs Mapping focuses on the
            families table alone; Breaches has its own dedicated view). */}
        {subTab === "overview" && (
        <CVEDashboardWidgets
          data={dashboard}
          loading={dashboardLoading}
          onSelectFamily={(name) => {
            setSelectedFamily(name);
            loadDetail(name);
            // Scroll the drill-down into view once the state settles.
            setTimeout(() => {
              const el = document.querySelector('[data-testid="family-detail-card"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 100);
          }}
          onJumpToUnmapped={() => {
            setSubTab("unmapped");
            setUnmappedOnly(true);
            setVulnerableOnly(false);
          }}
          onOpenQueue={() => setSubTab("priority")}
          onOpenBreaches={() => setSubTab("breaches")}
          onRefresh={() => { fetchDashboard(); fetchFamilies(); }}
        />
        )}

        {/* Needs-Mapping banner — reinforces the CTA above the families
            table so admins understand what this view is for and why it
            matters. */}
        {subTab === "unmapped" && (
          <div
            className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3"
            data-testid="unmapped-banner"
          >
            <HelpCircle className="w-5 h-5 text-amber-300 mt-0.5" />
            <div className="text-sm text-amber-100">
              <div className="font-semibold text-amber-200">
                {dashboard?.mapping_coverage?.unmapped_families || 0} families need a CPE mapping
              </div>
              <div className="text-amber-100/80 mt-0.5">
                Without a CPE, ALICE cannot match these applications to any CVE —
                <span className="font-semibold"> {(dashboard?.mapping_coverage?.unmapped_installs || 0).toLocaleString()} installs</span> it cannot see yet.
                Set the vendor:product CPE on each row to bring them into the picture; the biggest families are listed first on the Overview.
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Families column */}
          <div
            className="col-span-12 xl:col-span-7 min-w-0 bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] rounded-xl overflow-hidden"
            data-testid="families-card"
          >
            <div className="p-5 border-b border-[rgba(229,239,250,0.1)]">
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                <span className="text-base font-semibold text-white shrink-0">
                  Families ({search.trim() ? `${filteredFamilies.length.toLocaleString()} of ${familiesTotal.toLocaleString()}` : familiesTotal.toLocaleString()})
                </span>
                <div className="flex flex-wrap items-center justify-end gap-2 min-w-0 flex-1">
                  <div className="relative flex-1 min-w-[10rem] max-w-[14rem]">
                    <Search className="w-4 h-4 absolute left-2 top-2.5 text-white/50" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Filter families…"
                      className="pl-8 w-full h-9 bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white placeholder:text-white/40"
                      data-testid="family-search-input"
                    />
                  </div>
                  <Button
                    variant={vulnerableOnly ? "default" : "outline"}
                    onClick={() => {
                      setVulnerableOnly((v) => !v);
                      if (!vulnerableOnly) setUnmappedOnly(false);
                    }}
                    size="sm"
                    className={
                      vulnerableOnly
                        ? "bg-red-600 hover:bg-red-500 text-white"
                        : "bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white hover:bg-white/10"
                    }
                    data-testid="vulnerable-only-toggle"
                  >
                    <ShieldAlert className="w-4 h-4 mr-1" /> Vulnerable only
                  </Button>
                  <Button
                    variant={unmappedOnly ? "default" : "outline"}
                    onClick={() => {
                      setUnmappedOnly((v) => !v);
                      if (!unmappedOnly) setVulnerableOnly(false);
                    }}
                    size="sm"
                    className={
                      unmappedOnly
                        ? "bg-amber-600 hover:bg-amber-500 text-white"
                        : "bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white hover:bg-white/10"
                    }
                    data-testid="unmapped-only-toggle"
                  >
                    <HelpCircle className="w-4 h-4 mr-1" /> Unmapped only
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleAutoLink}
                    disabled={autoLinking}
                    size="sm"
                    className="bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white hover:bg-white/10"
                    title="Map every unmapped family that can be identified confidently against the CVE data's own vendor:product list. Ambiguous families are left for review and existing mappings are never overwritten."
                    data-testid="cpe-auto-link-btn"
                  >
                    <Wand2 className={`w-4 h-4 mr-1 ${autoLinking ? "animate-pulse" : ""}`} />
                    {autoLinking ? "Mapping…" : "Auto-map"}
                  </Button>
                </div>
              </div>
            </div>
            {/* No forced min-width — the remaining columns (Family / CPE
                / Apps / Devices) fit in any viewport without needing a
                horizontal scrollbar. */}
            <div className="max-h-[640px] overflow-y-auto">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow className="border-b-[rgba(229,239,250,0.1)] hover:bg-transparent">
                    <TableHead className="text-white font-semibold">Family</TableHead>
                    <TableHead className="text-white font-semibold w-[240px]">CPE (vendor:product)</TableHead>
                    <TableHead className="text-white font-semibold w-[80px] text-right">Apps</TableHead>
                    <TableHead className="text-white font-semibold w-[90px] text-right">Devices</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {familiesLoading && (
                    <TableRow><TableCell colSpan={4} className="text-center py-6 text-white/70">Loading…</TableCell></TableRow>
                  )}
                  {!familiesLoading && filteredFamilies.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center py-6 text-white/60">No families</TableCell></TableRow>
                  )}
                  {visibleFamilies.map((f) => {
                    const summary = f.cve_summary || {};
                    const isEditing = editing[f.family_name] !== undefined;
                    const isSelected = selectedFamily === f.family_name;
                    return (
                      <TableRow
                        key={f.family_name}
                        className={`border-b-[rgba(229,239,250,0.06)] hover:bg-white/[0.04] cursor-pointer ${isSelected ? "bg-cyan-500/10" : ""}`}
                        data-testid={`family-row-${f.family_name}`}
                      >
                        <TableCell>
                          <button
                            onClick={() => loadDetail(f.family_name)}
                            className="text-left text-white hover:text-cyan-300 font-medium break-words"
                          >
                            {f.family_name}
                            {summary.has_kev && <span className="ml-2 inline-block align-middle"><KevBadge on={true} /></span>}
                          </button>
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <div className="flex items-center gap-1">
                              <Input
                                value={editing[f.family_name]}
                                onChange={(e) => setEditing((s) => ({ ...s, [f.family_name]: e.target.value }))}
                                placeholder="vendor:product"
                                className="h-8 text-xs font-mono bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white placeholder:text-white/40"
                                data-testid={`cpe-input-${f.family_name}`}
                              />
                              <Button
                                size="icon"
                                className="h-7 w-7 bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleSaveCpe(f.family_name)}
                                data-testid={`cpe-save-${f.family_name}`}
                              >
                                <Save className="w-3 h-3" />
                              </Button>
                              <Button
                                size="icon"
                                variant="outline"
                                className="h-7 w-7 bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white"
                                onClick={() => setEditing((s) => {
                                  const next = { ...s }; delete next[f.family_name]; return next;
                                })}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            f.cpe ? (
                              <button
                                className="font-mono text-xs text-white/85 hover:text-cyan-300 text-left break-all"
                                onClick={() => setEditing((s) => ({ ...s, [f.family_name]: f.cpe || "" }))}
                                data-testid={`cpe-edit-${f.family_name}`}
                              >
                                {f.cpe}
                              </button>
                            ) : (
                              // Unmapped family — this is a hidden-risk row.
                              // Replace the ambiguous "click to set" with a
                              // visible amber "Not mapped — action required"
                              // pill so operators can spot them at a glance.
                              <div className="space-y-1">
                                <button
                                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-amber-100 bg-amber-500/20 border border-amber-500/40 hover:bg-amber-500/30"
                                  onClick={() => setEditing((s) => ({ ...s, [f.family_name]: "" }))}
                                  data-testid={`cpe-unmapped-${f.family_name}`}
                                  title="This family has no CPE mapping — click to add one. Without a CPE, ALICE cannot determine vulnerability."
                                >
                                  <HelpCircle className="w-3 h-3" />
                                  Not mapped — action required
                                </button>
                                {/* Candidates from the CVE data's own
                                    vendor:product vocabulary. One click maps
                                    the family; the confidence tier is shown
                                    so an operator knows what they are
                                    accepting. */}
                                {(cpeSuggestions[f.family_name] || []).length > 0 && (
                                  <div
                                    className="flex flex-wrap items-center gap-1"
                                    data-testid={`cpe-suggestions-${f.family_name}`}
                                  >
                                    <span className="text-[10px] uppercase tracking-wide text-white/40">
                                      Suggested
                                    </span>
                                    {(cpeSuggestions[f.family_name] || []).slice(0, 3).map((sg) => (
                                      <button
                                        key={sg.cpe}
                                        onClick={() => {
                                          setEditing((st) => ({ ...st, [f.family_name]: sg.cpe }));
                                          handleSaveCpe(f.family_name, sg.cpe);
                                        }}
                                        title={sg.reason}
                                        className={`font-mono text-[10px] rounded px-1.5 py-0.5 border transition-colors ${
                                          sg.auto_appliable
                                            ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25"
                                            : "bg-white/5 border-white/20 text-white/70 hover:bg-white/10"
                                        }`}
                                        data-testid={`cpe-suggestion-${f.family_name}-${sg.cpe}`}
                                      >
                                        {sg.cpe}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-white/85 text-right">
                          {summary.vulnerable_app_count ?? 0}
                        </TableCell>
                        <TableCell className="text-sm text-white/85 text-right">
                          {summary.vulnerable_device_count ?? 0}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {filteredFamilies.length > visibleFamilies.length && (
                <div className="px-5 py-2 text-[11px] text-white/55 border-t border-white/[0.08]" data-testid="families-render-cap">
                  Showing {visibleFamilies.length.toLocaleString()} of {filteredFamilies.length.toLocaleString()} — type in the filter to narrow.
                </div>
              )}
            </div>
          </div>

          {/* Detail column */}
          <div
            className="col-span-12 xl:col-span-5 min-w-0 bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] rounded-xl overflow-hidden"
            data-testid="family-detail-card"
          >
            <div className="p-5 border-b border-[rgba(229,239,250,0.1)]">
              <span className="text-base font-semibold text-white">
                {selectedFamily || "Pick a family on the left"}
              </span>
            </div>
            <div className="p-5">
              {!selectedFamily && (
                <p className="text-sm text-white/60 py-6 text-center">
                  Click a family name to see its CVEs and the safe / vulnerable verdict for each version.
                </p>
              )}
              {selectedFamily && detailLoading && (
                <p className="text-sm text-white/60 py-6 text-center">Loading…</p>
              )}
              {selectedFamily && detail && (
                <FamilyDetail detail={detail} />
              )}
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

const FamilyDetail = ({ detail }) => {
  const { family, candidate_cves = [], apps = [] } = detail;
  const summary = family?.cve_summary || {};
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-[rgba(229,239,250,0.12)] bg-[rgba(229,239,250,0.05)] p-4">
        <div className="text-xs text-white/60 mb-1">CPE</div>
        <div className="font-mono text-sm text-white">{family?.cpe || "—"}</div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Stat label="Vulnerable apps" value={summary.vulnerable_app_count ?? 0} />
          <Stat label="Affected devices" value={summary.vulnerable_device_count ?? 0} />
          <Stat
            label="Highest CVSS"
            value={
              summary.highest_cvss
                ? summary.highest_cvss.toFixed(1)
                : (summary.vulnerable_app_count > 0 ? "Unscored" : "—")
            }
          />
          <Stat label="Max EPSS" value={summary.max_epss != null ? (summary.max_epss * 100).toFixed(1) + "%" : "—"} />
        </div>
        <div className="text-xs text-white/50 mt-3">
          Evaluated: {fmtDateTime(summary.last_evaluated)}
        </div>
      </div>

      {/* Per-version verdicts: the customer-critical view */}
      <section>
        <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-cyan-300" />
          Per-version verdicts ({apps.length})
        </h3>
        <div className="rounded-lg border border-[rgba(229,239,250,0.12)] overflow-auto">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="border-b-[rgba(229,239,250,0.1)] hover:bg-transparent">
                <TableHead className="text-white/90 font-semibold">Version</TableHead>
                <TableHead className="text-white/90 font-semibold w-[96px]">Status</TableHead>
                <TableHead className="text-white/90 font-semibold w-[68px] text-right">Devices</TableHead>
                <TableHead className="text-white/90 font-semibold w-[60px] text-right">CVEs</TableHead>
                <TableHead className="text-white/90 font-semibold w-[96px]">Highest</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apps.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-3 text-white/60 text-sm">No detected apps</TableCell></TableRow>
              )}
              {apps.map((app, idx) => {
                const st = app.cve_status || {};
                return (
                  <TableRow
                    key={`${app.display_name}-${app.version}-${idx}`}
                    className="border-b-[rgba(229,239,250,0.06)] hover:bg-white/[0.04]"
                    data-testid={`app-version-row-${app.version}`}
                  >
                    <TableCell className="font-mono text-xs text-white break-all">
                      {app.version || <span className="italic text-white/40">no version</span>}
                    </TableCell>
                    <TableCell>
                      {st.is_vulnerable ? (
                        <Badge className="bg-red-600 text-white text-xs" data-testid={`status-${app.version}-vulnerable`}>
                          Vulnerable
                        </Badge>
                      ) : (
                        <span
                          className="text-white/40 text-xs"
                          data-testid={`status-${app.version}-unmatched`}
                          title="No CVE match — could be safe or unmapped. Never advertised as 'safe' to avoid false negatives."
                        >
                          —
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-white/85 text-right">{app.device_count ?? 0}</TableCell>
                    <TableCell className="text-sm text-white/85 text-right">{st.cve_count ?? 0}</TableCell>
                    <TableCell>
                      <SeverityBadge
                        severity={st.highest_severity}
                        unscored={st.is_vulnerable && !st.highest_severity}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* Candidate CVE list */}
      <section>
        <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-cyan-300" />
          Candidate CVEs ({candidate_cves.length})
        </h3>
        <div className="rounded-lg border border-[rgba(229,239,250,0.12)] max-h-[320px] overflow-auto">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="border-b-[rgba(229,239,250,0.1)] hover:bg-transparent">
                <TableHead className="text-white/90 font-semibold">CVE</TableHead>
                <TableHead className="text-white/90 font-semibold w-[92px]">Severity</TableHead>
                <TableHead className="text-white/90 font-semibold w-[60px] text-right">CVSS</TableHead>
                <TableHead className="text-white/90 font-semibold w-[60px] text-right">EPSS</TableHead>
                <TableHead className="text-white/90 font-semibold w-[120px]">Affected range</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {candidate_cves.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-3 text-white/60 text-sm">No candidate CVEs for this CPE</TableCell></TableRow>
              )}
              {candidate_cves
                .filter((c) => c && c.cve_id)
                .map((c) => {
                const range = c.exact_version
                  ? `= ${c.exact_version}`
                  : [
                      c.version_start_incl ? `≥ ${c.version_start_incl}` : c.version_start_excl ? `> ${c.version_start_excl}` : "",
                      c.version_end_incl ? `≤ ${c.version_end_incl}` : c.version_end_excl ? `< ${c.version_end_excl}` : "",
                    ].filter(Boolean).join(", ");
                return (
                  <TableRow
                    key={c.cve_id}
                    className="border-b-[rgba(229,239,250,0.06)] hover:bg-white/[0.04]"
                    data-testid={`cve-row-${c.cve_id}`}
                  >
                    <TableCell className="font-mono text-xs break-words">
                      <a
                        href={`https://nvd.nist.gov/vuln/detail/${c.cve_id}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1"
                      >
                        {c.cve_id} <ExternalLink className="w-3 h-3" />
                      </a>
                      {c.kev && <span className="ml-2"><KevBadge on={true} /></span>}
                    </TableCell>
                    <TableCell><SeverityBadge severity={c.cvss_severity} unscored={!c.cvss_severity} /></TableCell>
                    <TableCell className="text-sm text-white/85 text-right">{c.cvss_score ?? "—"}</TableCell>
                    <TableCell className="text-sm text-white/85 text-right">
                      {c.epss_score != null ? (c.epss_score * 100).toFixed(1) + "%" : "—"}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-white/85 break-words">{range || "all"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded-md bg-[rgba(229,239,250,0.05)] border border-[rgba(229,239,250,0.08)] p-2">
    <div className="text-xs text-white/60">{label}</div>
    <div className="text-lg font-semibold text-white">{value}</div>
  </div>
);

export default CVEManagementPage;
